import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  Row,
  RowData,
} from "@tanstack/react-table";
import { CSSProperties, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useReactTableNoMemo } from "#shared/helpers/useReactTableNoMemo";
import { Icon } from "#shared/icons/Icon";
import { EllipsisTooltip } from "#shared/view/components/EditableTable/EllipsisTooltip";
import { Group } from "#shared/view/components/Group";
import { Pagination } from "#shared/view/components/Pagination";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { Table } from "#shared/view/designSystem/Table";

declare module "@tanstack/react-table" {
  // https://tanstack.com/table/latest/docs/api/core/table#meta
  // We need to redeclare the interface to augment it. The unused-vars is a false positive in this case.
  // oxlint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    updateData: (rowId: string, columnId: string, value: unknown) => void;
  }
}

type EditableTableColMeta =
  | {
      width?: string | number;
      headerRight?: React.ReactNode;
    }
  | undefined;

type RowDragHandleCellProps = {
  rowId: string;
};

const RowDragHandleCell = ({ rowId }: RowDragHandleCellProps) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });
  return (
    <span
      {...attributes}
      {...listeners}
      style={{ cursor: "grab", display: "flex", alignItems: "center" }}
    >
      <Icon name="gripVertical" size={12} />
    </span>
  );
};

type DraggableRowProps<T> = {
  row: Row<T>;
  children: React.ReactNode;
};

const DraggableRow = <T,>({ row, children }: DraggableRowProps<T>) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.id,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
  };

  return (
    <Table.Tr ref={setNodeRef} style={style}>
      {children}
    </Table.Tr>
  );
};

type EditableTableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  defaultColumn: Partial<ColumnDef<T>>;
  getRowId?: (row: T, index: number) => string;
  onUpdateCell: (rowId: string, columnId: string, value: unknown) => void;
  onAddRow: () => void;
  sortable?: boolean;
  onReorder?: (data: T[]) => void;
  pageSize?: number;
};

export const EditableTable = <T,>({
  data,
  columns,
  defaultColumn,
  getRowId,
  onUpdateCell,
  onAddRow,
  sortable = false,
  onReorder,
  pageSize,
}: EditableTableProps<T>) => {
  "use no memo";
  const { t } = useTranslation();
  const paginated = pageSize !== undefined;

  const columnsWithDragHandle = useMemo<ColumnDef<T>[]>(() => {
    if (!sortable) {
      return columns;
    }
    const dragHandleColumn: ColumnDef<T> = {
      id: "drag-handle",
      header: "",
      cell: ({ row }) => <RowDragHandleCell rowId={row.id} />,
      meta: { width: 40 },
    };
    return [dragHandleColumn, ...columns];
  }, [sortable, columns]);

  const { table, rows, headerGroups, leafColumns, pageCount, state } = useReactTableNoMemo({
    data,
    columns: columnsWithDragHandle,
    defaultColumn,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    ...(paginated && {
      getPaginationRowModel: getPaginationRowModel(),
      initialState: { pagination: { pageSize, pageIndex: 0 } },
      autoResetPageIndex: false,
    }),
    meta: {
      updateData: onUpdateCell,
    },
  });

  const dataIds = rows.map((row) => row.id) as UniqueIdentifier[];

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && onReorder) {
      const oldIndex = dataIds.indexOf(active.id);
      const newIndex = dataIds.indexOf(over.id);
      const reorderedData = arrayMove([...data], oldIndex, newIndex);
      onReorder(reorderedData);
    }
  };

  const tableContent = (
    <Stack gap="sm">
      <Table highlightOnHover stickyHeader style={{ tableLayout: "fixed" }}>
        <colgroup>
          {leafColumns.map((col) => {
            const columnsCount = leafColumns.length;
            const defaultWidth = 100 / columnsCount;
            const w = (col.columnDef.meta as EditableTableColMeta)?.width || `${defaultWidth}%`;
            const style = { width: typeof w === "number" ? `${w}px` : String(w) };
            return <col key={col.id} style={style} />;
          })}
        </colgroup>
        <Table.Thead>
          {headerGroups.map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.Th key={header.id}>
                  <Group gap="xxxs">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {(header.column.columnDef.meta as EditableTableColMeta)?.headerRight}
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {sortable ? (
            <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
              {rows.map((row) => (
                <DraggableRow key={row.id} row={row}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td key={cell.id}>
                      <EllipsisTooltip
                        content={flexRender(cell.column.columnDef.cell, cell.getContext())}
                      />
                    </Table.Td>
                  ))}
                </DraggableRow>
              ))}
            </SortableContext>
          ) : (
            rows.map((row) => (
              <Table.Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Td key={cell.id}>
                    <EllipsisTooltip
                      content={flexRender(cell.column.columnDef.cell, cell.getContext())}
                    />
                  </Table.Td>
                ))}
              </Table.Tr>
            ))
          )}
          {!paginated && (
            <Table.Tr>
              <Table.Td colSpan={columnsWithDragHandle.length}>
                <Button onClick={onAddRow} variant="outline" leftSection={<Icon name="plus" />}>
                  {t("common.add")}
                </Button>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      {paginated && (
        <Group justify="space-between">
          <Button onClick={onAddRow} variant="outline" leftSection={<Icon name="plus" />}>
            {t("common.add")}
          </Button>
          {pageCount > 1 && (
            <Pagination
              total={pageCount}
              value={state.pagination.pageIndex + 1}
              onChange={(page) => table.setPageIndex(page - 1)}
            />
          )}
        </Group>
      )}
    </Stack>
  );

  if (!sortable) {
    return tableContent;
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      {tableContent}
    </DndContext>
  );
};
